import { resolveSchoolHistoryYear, schoolRuleTimeZone, SchoolRulePeriodError } from './schoolRulePeriods.js';

/** Keep a history cutoff current without inventing a second school calendar. */
export function watchSchoolHistoryYear({ listen, receive, onError, onPending = () => {},
  onResume = () => () => {}, now = () => new Date() }) {
  let active = true;
  let timer;
  let key = null;
  let errorKey = null;
  const inputs = {};
  const clear = () => { clearTimeout(timer); timer = null; };
  const schedule = delay => {
    clear();
    timer = setTimeout(resolve, Math.min(2147483647, Math.max(1, delay)));
  };
  const fail = error => {
    if (!active) return;
    key = null;
    clear();
    const nextErrorKey = `${error?.code}:${error?.message}`;
    if (nextErrorKey !== errorKey) { errorKey = nextErrorKey; onError(error); }
  };
  const resolve = () => {
    if (!active) return;
    const readError = inputs.school?.error || inputs.periods?.error;
    if (readError) { fail(readError); return; }
    if (!inputs.school?.confirmed || !inputs.periods?.confirmed) {
      clear();
      key = null; errorKey = null;
      onPending();
      return;
    }
    let bounds;
    const instant = now();
    try {
      if (!inputs.school.data) throw new SchoolRulePeriodError('The school is unavailable. Retry after its configuration is available.');
      bounds = resolveSchoolHistoryYear(inputs.periods.data, instant, schoolRuleTimeZone(inputs.school.data));
    } catch (error) {
      fail(error);
      // A configured gap can end while the screen is still open. This only
      // re-evaluates confirmed settings; it does not poll or fabricate dates.
      schedule(60000);
      return;
    }
    errorKey = null;
    const nextKey = `${bounds.start.getTime()}:${bounds.end.getTime()}:${bounds.source}`;
    schedule(bounds.end.getTime() + 1 - instant.getTime());
    if (nextKey !== key) { key = nextKey; receive(bounds); }
  };
  const stops = [];
  let stopResume;
  const stop = () => {
    if (!active) return;
    active = false; clear(); stops.forEach(unsubscribe => unsubscribe?.()); stopResume?.();
  };
  try {
    for (const kind of ['school', 'periods']) {
      stops.push(listen(kind, (data, metadata = {}) => {
        if (!active) return;
        inputs[kind] = { data, confirmed: metadata.fromCache !== true && metadata.hasPendingWrites !== true };
        resolve();
      }, error => {
        if (!active) return;
        const failure = new SchoolRulePeriodError(error?.message || 'Could not read Year dates from Time Periods.');
        inputs[kind] = { confirmed: false, error: failure };
        fail(failure);
      }));
    }
    stopResume = onResume(resolve);
  } catch (error) {
    fail(error);
    stop();
  }
  return stop;
}
